import React from 'react'
import Skeleton from 'react-loading-skeleton'

import { FormattedError } from '@app/common/Components/FormattedError'
import {
  AlertType,
  useNotifications
} from '@app/common/services/Notifications/NotificationsProvider'
import { ChatChronicItemType, MessageType } from '@app/graphql/types'
import { MessageBox } from '@app/lib/components/MessageBox'
import { useHandleFileUpload } from '@app/lib/hooks/useHandleFileUpload'
import { useSmartPolling } from '@app/lib/hooks/useSmartPolling'
import { useAttachDocumentToLawyerMessageMutation } from '@app/queries/AttachDocumentToLawyerMessage/AttachDocumentToLawyerMessage.queries'
import {
  CourtCaseEventFragment,
  CourtCaseMessageFragment
} from '@app/queries/CourtCases/CourtCase.queries'

import { useCourtCaseContext } from '../CourtCaseContext/CourtCaseContext'

import { CourtCaseCustomer } from './Components/CourtCaseCustomer'
import { CourtCaseEventBox } from './Components/CourtCaseEventBox'
import { CourtCaseLawyer } from './Components/CourtCaseLawyer'
import { uploadFiles } from './helpers/uploadFiles'
import { useUpsertCourtCaseMessage } from './hooks/useUpsertCourtCaseMessage'

export const ChatChronic = () => {
  const { data, loading, refetch } = useCourtCaseContext()
  const { upsertMessage } = useUpsertCourtCaseMessage()
  const { pushNotification } = useNotifications()
  const [attachDocumentToLawyerMessageMutation] = useAttachDocumentToLawyerMessageMutation()
  const { files, setFiles, addFile, removeFile } = useHandleFileUpload()

  useSmartPolling({
    onPoll: refetch,
    enabled: !loading && !!data
  })

  if (loading || !data) return <Skeleton count={3} />

  const lastCustomerMessage = [...data.chatChronic]
    .filter(
      (c) =>
        c.type === ChatChronicItemType.Message &&
        (c.item as CourtCaseMessageFragment).type === MessageType.CourtCaseCustomer
    )
    .pop()

  const handleSubmit = async (message: string) => {
    try {
      const messageResult = await upsertMessage({
        content: message,
        courtCaseUuid: data.uuid,
        customerId: data.customer.id,
        type: MessageType.CourtCaseLawyer,
        lawyerId: data.assignedLawyer?.id
      })

      if (files.length && messageResult?.id) {
        await uploadFiles({
          files,
          messageId: messageResult.id,
          attachDocumentToLawyerMessageMutation
        })
        setFiles([])
      }

      await refetch()
    } catch (err) {
      pushNotification({
        type: AlertType.error,
        message: <FormattedError error={err} />
      })
    }
  }

  return (
    <div>
      {data.chatChronic.map((item, idx) => {
        if (item.type === ChatChronicItemType.Event) {
          return <CourtCaseEventBox key={idx} event={item.item as CourtCaseEventFragment} />
        }
        if ((item.item as CourtCaseMessageFragment).type === MessageType.CourtCaseLawyer) {
          return <CourtCaseLawyer key={idx} message={item.item as CourtCaseMessageFragment} />
        }
        if ((item.item as CourtCaseMessageFragment).type === MessageType.CourtCaseCustomer) {
          return (
            <CourtCaseCustomer
              key={idx}
              message={item.item as CourtCaseMessageFragment}
              isLatestQuestion={
                (item.item as CourtCaseMessageFragment).id === lastCustomerMessage?.id
              }
            />
          )
        }
        return null
      })}
      <MessageBox
        onFileUpload={addFile}
        onFileRemove={removeFile}
        onSubmit={handleSubmit}
        files={files}
      />
    </div>
  )
}
